/**
 * Meta Pixel event helper.
 *
 * The pixel itself is bootstrapped in /app/frontend/public/index.html
 * (window.fbq, PageView fires on initial load; AppContent fires a
 * PageView on every route change). This module adds tiny ergonomic
 * wrappers for the two conversion events we actively use for
 * Facebook campaign optimization.
 *
 * All helpers no-op silently when `window.fbq` is undefined — happens
 * when the user has an ad-blocker, the external script is blocked by
 * their network, or during SSR/tests. We never want pixel calls to
 * throw into a user-facing flow.
 */

function _fire(eventName, params) {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq !== 'function') return;
  try {
    if (params === undefined) {
      window.fbq('track', eventName);
    } else {
      window.fbq('track', eventName, params);
    }
  } catch (_e) {
    // Pixel errors should never leak into the real user flow.
  }
}

/**
 * Fire a `Lead` standard event — called when a user creates a new
 * account (email+password register OR first-ever Google sign-in).
 * NOT fired for returning sign-ins. Facebook treats Lead as the
 * top-of-funnel conversion signal for optimization.
 */
export function trackLead(signupMethod) {
  _fire('Lead', signupMethod ? { signup_method: signupMethod } : undefined);
}

/**
 * Fire a `CompleteRegistration` standard event — called when a user
 * successfully verifies their email address. This is the "activated
 * user" signal (one step further down-funnel than `Lead`), so Facebook
 * can bid for users most likely to actually complete signup, not just
 * create a stale/unverified account.
 */
export function trackCompleteRegistration({ method } = {}) {
  _fire('CompleteRegistration', method ? { registration_method: method } : undefined);
}

/**
 * Fire an `InitiateCheckout` standard event — called when a user picks
 * a paid plan on the Pricing page (opens the PayPal buttons). This is
 * the top-of-checkout-funnel signal that pairs with `Subscribe` so
 * Facebook can measure the Pricing-page-view → Subscribe conversion
 * rate and optimize for the whole funnel.
 */
export function trackInitiateCheckout({ value, currency = 'USD', planId } = {}) {
  const params = { currency };
  if (typeof value === 'number' && !Number.isNaN(value)) params.value = value;
  if (planId) params.plan_id = planId;
  _fire('InitiateCheckout', params);
}

/**
 * Fire a `Subscribe` standard event — called when a PayPal
 * subscription approval returns successfully. Includes value +
 * currency so Facebook can optimize for revenue, not just events.
 *
 * @param {Object} params
 * @param {number} params.value   subscription amount (e.g. 24 for monthly, 199 for yearly)
 * @param {string} params.currency  ISO 4217 code (default 'USD')
 * @param {string} [params.predictedLtv]  optional lifetime value estimate
 * @param {string} [params.planId]  our plan id for custom-data tracking
 */
// Custom event (trackCustom) so it never pollutes the paid `Subscribe` signal.
export function trackYouTubeChannelClick(source = 'landing_hero') {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq('trackCustom', 'YouTubeChannelClick', { source });
  } catch (_e) {}
}

export function trackSubscribe({ value, currency = 'USD', predictedLtv, planId } = {}) {
  const params = { currency };
  if (typeof value === 'number' && !Number.isNaN(value)) params.value = value;
  if (predictedLtv) params.predicted_ltv = predictedLtv;
  if (planId) params.plan_id = planId;
  _fire('Subscribe', params);
}
